import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Bot,
  Power,
  RefreshCw,
  Loader2,
  AlertTriangle,
  TrendingUp,
  MessageCircle,
  Mic,
  Download,
  CheckCircle2,
  Circle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

const TIER_STYLES = {
  ready:  { bg: "bg-emerald-100",  text: "text-emerald-800",  label: "Ready" },
  almost: { bg: "bg-amber-100",   text: "text-amber-800",   label: "Almost" },
  early:  { bg: "bg-gray-100",    text: "text-gray-700",    label: "Early"  },
};

const GROWTH_STYLES = {
  improving:       { bg: "bg-emerald-50",  border: "border-emerald-200",  text: "text-emerald-700",  label: "📈 Improving" },
  high_performer:  { bg: "bg-yellow-50",   border: "border-yellow-300",   text: "text-yellow-800",   label: "⭐ High performer" },
  needs_support:   { bg: "bg-rose-50",     border: "border-rose-200",     text: "text-rose-700",     label: "🤝 Needs support" },
};

/**
 * Tiny inline SVG sparkline of pitch-score history. No external chart lib needed.
 * `points` is an array of percents (0-100). Renders nothing for <2 points.
 */
function PitchSparkline({ points = [], width = 88, height = 24 }) {
  if (!points || points.length < 2) {
    return (
      <span className="text-xs text-gray-400">
        {points.length === 1 ? `${points[0]}%` : "—"}
      </span>
    );
  }
  const padX = 2;
  const padY = 3;
  const stepX = (width - padX * 2) / (points.length - 1);
  const last = points[points.length - 1];
  const first = points[0];
  const best = Math.max(...points);
  const delta = last - first;
  const trend = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const ariaSummary =
    `Pitch trajectory across ${points.length} attempts: ` +
    `started at ${first}%, best ${best}%, latest ${last}%, ` +
    `${trend === "flat" ? "no change" : `${trend} ${Math.abs(delta)} percentage points`}.`;
  const polyPoints = points
    .map((p, i) => {
      const x = padX + i * stepX;
      const y = height - padY - ((p / 100) * (height - padY * 2));
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const lastX = padX + (points.length - 1) * stepX;
  const lastY = height - padY - ((last / 100) * (height - padY * 2));
  return (
    <span className="inline-flex items-center">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaSummary}
        data-testid="pitch-sparkline"
        focusable="false"
      >
        <polyline
          points={polyPoints}
          fill="none"
          stroke="#f59e0b"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx={lastX} cy={lastY} r="2" fill="#f59e0b" />
      </svg>
      <span className="sr-only">{ariaSummary}</span>
    </span>
  );
}

function StatPill({ label, value, accent = "amber" }) {
  return (
    <div
      className={`rounded-lg border bg-white p-3 ${
        accent === "emerald"
          ? "border-emerald-200"
          : accent === "purple"
          ? "border-purple-200"
          : "border-amber-200"
      }`}
    >
      <div className="text-[11px] uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div
        className={`text-2xl font-bold ${
          accent === "emerald"
            ? "text-emerald-700"
            : accent === "purple"
            ? "text-purple-700"
            : "text-amber-700"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function EducatorLemonBotPanel({ classId }) {
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [questionThemes, setQuestionThemes] = useState([]);
  const [pitch, setPitch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingToggle, setSavingToggle] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [error, setError] = useState(null);
  const [showStudent, setShowStudent] = useState(null); // user_id whose rubric is expanded

  const fetchAll = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    setError(null);
    try {
      const [s, u, q, p] = await Promise.all([
        axios.get(`${API}/educator/lemonbot/settings/${classId}`, authHeaders()),
        axios.get(`${API}/educator/lemonbot/usage?class_id=${classId}`, authHeaders()),
        axios.get(`${API}/educator/lemonbot/common-questions?class_id=${classId}`, authHeaders()),
        axios.get(`${API}/educator/lemonbot/pitch-readiness?class_id=${classId}`, authHeaders()),
      ]);
      setSettings(s.data);
      setUsage(u.data);
      setQuestions(q.data?.questions || []);
      setQuestionThemes(q.data?.themes || []);
      setPitch(p.data?.students || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load LemonBot data");
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleToggle = async () => {
    if (!settings) return;
    setSavingToggle(true);
    try {
      await axios.post(
        `${API}/educator/lemonbot/settings/${classId}`,
        { enabled: !settings.class_enabled },
        authHeaders()
      );
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save toggle");
    } finally {
      setSavingToggle(false);
    }
  };

  const handleDownloadCsv = async () => {
    setDownloadingCsv(true);
    try {
      const res = await axios.get(
        `${API}/educator/business-plan/csv?class_id=${classId}`,
        { ...authHeaders(), responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `business_plan_progress_${classId.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || "CSV download failed");
    } finally {
      setDownloadingCsv(false);
    }
  };

  if (!classId) return null;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const classOn = !!settings?.class_enabled;
  const globalOn = !!settings?.global_enabled;
  const effectiveOn = classOn && globalOn;
  const totalMessages = usage?.total_messages || 0;
  const studentCount = (usage?.by_student || []).length;
  const intentTop = (usage?.by_intent || [])[0];

  return (
    <div className="space-y-6" data-testid="educator-lemonbot-panel">
      {/* Toggle hero */}
      <div
        className={`rounded-2xl p-6 text-white shadow-lg ${
          effectiveOn
            ? "bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500"
            : "bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900"
        }`}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Bot className="w-6 h-6" />
              <h2 className="text-2xl font-bold">LemonBot for this class</h2>
            </div>
            <p className="text-white/85 max-w-xl text-sm">
              Toggle LemonBot on or off for the students enrolled in this class.
              {!globalOn && (
                <span className="block mt-2 bg-black/20 rounded px-2 py-1 text-xs">
                  ⚠ Admin has paused LemonBot platform-wide. Your toggle will
                  not take effect until that's re-enabled.
                </span>
              )}
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={savingToggle}
            data-testid="educator-lemonbot-toggle-btn"
            className={`inline-flex items-center gap-2 font-semibold px-5 py-3 rounded-xl shadow-md disabled:opacity-50 ${
              classOn
                ? "bg-white text-amber-700 hover:bg-amber-50"
                : "bg-emerald-500 text-white hover:bg-emerald-600"
            }`}
          >
            {savingToggle ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Power className="w-5 h-5" />
            )}
            {classOn ? "Turn OFF for class" : "Turn ON for class"}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Header w/ refresh + CSV */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-amber-500" />
          AI Usage & Readiness
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadCsv}
            disabled={downloadingCsv}
            data-testid="educator-lemonbot-download-csv-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50 px-3 py-1.5 rounded-lg"
          >
            {downloadingCsv ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download Plan CSV
          </button>
          <button
            onClick={fetchAll}
            disabled={loading}
            data-testid="educator-lemonbot-refresh-btn"
            className="text-sm font-medium text-gray-600 hover:text-gray-900 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Usage stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill label="Total Messages" value={totalMessages} accent="amber" />
        <StatPill label="Active Students" value={studentCount} accent="emerald" />
        <StatPill
          label="Top Intent"
          value={
            intentTop
              ? intentTop.intent.replace(/_/g, " ")
              : "—"
          }
          accent="purple"
        />
        <StatPill
          label="Common Qs"
          value={(questions || []).length}
          accent="amber"
        />
      </div>

      {/* Usage breakdown */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="usage-by-student">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-1.5">
            <MessageCircle className="w-4 h-4 text-amber-500" />
            Most Active Students
          </h4>
          {(usage?.by_student || []).length === 0 ? (
            <p className="text-sm text-gray-500">
              No messages yet. Once students chat with LemonBot, they'll show up here.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {(usage?.by_student || []).slice(0, 8).map((s) => (
                <li
                  key={s.user_id}
                  className="flex items-center justify-between py-2 text-sm"
                >
                  <span className="text-gray-800">{s.name}</span>
                  <span className="font-semibold text-amber-700">
                    {s.messages} msgs
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="usage-by-intent">
          <h4 className="font-semibold text-gray-800 mb-3">By Intent</h4>
          {(usage?.by_intent || []).length === 0 ? (
            <p className="text-sm text-gray-500">No intent data yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {(usage?.by_intent || []).map((i) => {
                const pct =
                  totalMessages > 0
                    ? Math.round((i.messages / totalMessages) * 100)
                    : 0;
                return (
                  <li key={i.intent} className="text-sm">
                    <div className="flex justify-between mb-0.5">
                      <span className="capitalize text-gray-700">
                        {i.intent.replace(/_/g, " ")}
                      </span>
                      <span className="text-gray-500">
                        {i.messages} ({pct}%)
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-orange-400"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="usage-by-lesson">
          <h4 className="font-semibold text-gray-800 mb-3">By Lesson</h4>
          {(usage?.by_lesson || []).length === 0 ? (
            <p className="text-sm text-gray-500">
              No lesson-tagged conversations yet.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {usage.by_lesson.slice(0, 10).map((l) => (
                <li key={l.lesson_id} className="flex justify-between py-2 text-sm">
                  <span className="text-gray-800 truncate pr-2">
                    {l.lesson_title}
                  </span>
                  <span className="font-semibold text-amber-700 flex-shrink-0">
                    {l.messages}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="usage-by-module">
          <h4 className="font-semibold text-gray-800 mb-3">By Module</h4>
          {(usage?.by_module || []).length === 0 ? (
            <p className="text-sm text-gray-500">
              No module-tagged conversations yet.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {usage.by_module.slice(0, 10).map((m) => (
                <li key={m.module_id} className="flex justify-between py-2 text-sm">
                  <span className="text-gray-800 truncate pr-2">
                    {m.module_title}
                  </span>
                  <span className="font-semibold text-amber-700 flex-shrink-0">
                    {m.messages}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Common questions — prefer AI-clustered themes when available */}
      <div
        className="bg-white rounded-2xl border border-gray-200 p-5"
        data-testid="common-questions"
      >
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-1.5">
          <MessageCircle className="w-4 h-4 text-purple-500" />
          Common Student Questions
          {questionThemes.length > 0 && (
            <span className="text-[10px] font-semibold uppercase tracking-wide bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 ml-1">
              AI grouped
            </span>
          )}
        </h3>

        {(questions || []).length === 0 ? (
          <p className="text-sm text-gray-500">
            Questions students type freely will be summarized here. Quick-action
            taps are excluded so this list shows what they actually ask.
          </p>
        ) : questionThemes.length > 0 ? (
          <ul className="space-y-3" data-testid="common-questions-themes">
            {questionThemes.map((t, i) => (
              <li
                key={i}
                className="bg-purple-50/60 border border-purple-200 rounded-lg p-3"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="font-semibold text-purple-900">
                    📌 {t.label}
                  </span>
                  <span className="text-xs bg-purple-200 text-purple-800 font-semibold rounded-full px-2 py-0.5">
                    {t.count}× total
                  </span>
                </div>
                <ul className="space-y-1.5 mt-1">
                  {t.questions.slice(0, 5).map((q, qi) => (
                    <li
                      key={qi}
                      className="text-sm bg-white border border-purple-100 rounded px-2 py-1.5 flex items-start justify-between gap-2"
                    >
                      <span className="text-gray-800">{q.examples[0]}</span>
                      <span className="text-xs text-purple-700 font-semibold flex-shrink-0">
                        {q.count}×
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="space-y-2">
            {questions.slice(0, 10).map((q, idx) => (
              <li
                key={idx}
                className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="text-gray-800">{q.examples[0]}</div>
                  <span className="flex-shrink-0 text-xs bg-amber-100 text-amber-800 font-semibold rounded-full px-2 py-0.5">
                    {q.count}×
                  </span>
                </div>
                {q.lessons && q.lessons.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    On: {q.lessons.join(" · ")}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pitch readiness */}
      <div
        className="bg-white rounded-2xl border border-gray-200 p-5"
        data-testid="pitch-readiness"
      >
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-1.5">
          <Mic className="w-4 h-4 text-emerald-500" />
          Pitch Readiness Score
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          A 100-point rubric combining each student's session-7 pitch
          reflections, capstone completeness, AI plan, and how often they've
          used LemonBot's pitch + plan coaching.
        </p>
        {(pitch || []).length === 0 ? (
          <p className="text-sm text-gray-500">No students enrolled.</p>
        ) : (
          <ul className="space-y-2">
            {pitch.map((s) => {
              const tier = TIER_STYLES[s.tier] || TIER_STYLES.early;
              const expanded = showStudent === s.user_id;
              const growth = s.growth || {};
              const growthStyle = GROWTH_STYLES[growth.label];
              return (
                <li
                  key={s.user_id}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() =>
                      setShowStudent(expanded ? null : s.user_id)
                    }
                    data-testid={`pitch-readiness-row-${s.user_id}`}
                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 text-left gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className="font-medium text-gray-800 truncate">
                        {s.name}
                      </span>
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tier.bg} ${tier.text} flex-shrink-0`}
                      >
                        {tier.label}
                      </span>
                      {growthStyle && (
                        <span
                          data-testid={`growth-label-${s.user_id}`}
                          className={`hidden sm:inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${growthStyle.bg} ${growthStyle.border} ${growthStyle.text}`}
                          title={
                            growth.attempts >= 2
                              ? `First: ${growth.first_pct}% · Best: ${growth.best_pct}% · Δ ${growth.delta >= 0 ? "+" : ""}${growth.delta}`
                              : `Best: ${growth.best_pct}%`
                          }
                        >
                          {growthStyle.label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <PitchSparkline points={growth.trajectory || []} />
                      <div className="w-24 hidden md:block">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${
                              s.score >= 80
                                ? "bg-emerald-500"
                                : s.score >= 50
                                ? "bg-amber-500"
                                : "bg-gray-400"
                            }`}
                            style={{ width: `${s.score}%` }}
                          />
                        </div>
                      </div>
                      <span className="font-bold text-gray-900 tabular-nums">
                        {s.score}/100
                      </span>
                    </div>
                  </button>
                  {expanded && (
                    <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                      {growth.attempts >= 1 && (
                        <div className="text-xs text-gray-600 mb-2">
                          Pitch trajectory:{" "}
                          <span className="font-mono text-gray-800">
                            {(growth.trajectory || []).join("% → ") + "%"}
                          </span>
                          {" · "}
                          {growth.attempts} attempt{growth.attempts === 1 ? "" : "s"}
                          {growth.delta !== null && growth.delta !== undefined && (
                            <span
                              className={
                                growth.delta >= 0
                                  ? "ml-2 text-emerald-700 font-semibold"
                                  : "ml-2 text-rose-700 font-semibold"
                              }
                            >
                              {growth.delta >= 0 ? "+" : ""}
                              {growth.delta}% growth
                            </span>
                          )}
                        </div>
                      )}
                      <ul className="space-y-1.5 text-sm">
                        {Object.entries(s.rubric).map(([key, r]) => (
                          <li
                            key={key}
                            className="flex items-center justify-between"
                          >
                            <span className="flex items-center gap-2 text-gray-700">
                              {r.earned > 0 ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <Circle className="w-4 h-4 text-gray-300" />
                              )}
                              {key.replace(/_/g, " ")}
                              {typeof r.count === "number" && (
                                <span className="text-xs text-gray-500">
                                  ({r.count} uses)
                                </span>
                              )}
                            </span>
                            <span className="font-mono text-xs text-gray-600">
                              {r.earned}/{r.max}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default EducatorLemonBotPanel;
